import React, { useState, useEffect, useRef, useMemo } from "react";
import { connect } from "react-redux";
import { compose } from "recompose";
import { Select, Row, Col, Empty } from "antd";
import { withTranslation } from "react-i18next";
import * as R from "ramda";
import { filter } from "fuzzaldrin";
import { Content } from "@worcket/common/components/layout";
import { Form, FormItem } from "@worcket/common/components/controls";
import { withForm, isLoading, dispatchOnMount } from "@worcket/common/hocs";
import { Range } from "@worcket/common/components/inputs";
import moment from "moment";

import * as gtag from "../../../../lib/gtag";
import StatusSelect from "../../../../components/inputs/StatusSelect";
import Multi from "../../../../components/inputs/Multi";
import Toggle from "../../../../components/inputs/Toggle";
import Formation from "../../../../components/inputs/Formation";
import { getLanguages } from "../../../../utils/data/static/languages";
import { normalizeText } from "../../../../utils/candidates";
import {
  saveOpportunityCandidate,
  SAVE_OPPORTUNITY_CANDIDATE,
  GO_TO_PREVIOUS_WIZARD_STEP
} from "../../../../store/actions/opportunity";
import {
  FETCH_PROFESSIONS,
  fetchSkills,
  FETCH_SKILLS,
  FETCH_UNIVERSITIES,
  FETCH_STUDIES
} from "../../../../store/actions/catalogs";
import Slider from "../../../../components/inputs/Slider";
import {
  FORMS_KEYS,
  EDUCATION_LEVELS
} from "../../../../constants/opportunity";
import MultipleAreaLocation from "../../../../components/controls/MultipleAreaLocation";
import { deleteOpportunityStoredFilters } from "../../../../utils/candidatesFilters";
import Button from "../../../../components/Button";
import { ACCEPT_LANGUAGE } from "../../../../locales/acceptedLanguages";
import { getCountries } from "../../../../utils/data/static/countries";

import ExcludingFilterInfo from "./ExcludingFilterInfo";

import styled from "styled-components/macro";

const sortOptions = R.sortBy(
  R.compose(
    R.toLower,
    R.prop("label")
  )
);

const experiences = [0, 0.25, 0.5, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const EmptyUniversities = styled(Empty)`
  svg {
    height: 100%;
  }
`;

const CandidateFormWrapper = styled.div`
  h2 + p {
    margin-bottom: 40px;
  }
  .slider-option {
    width: 200px;
    display: flex;
    justify-content: space-between;
    font-weight: 500;
  }
`;

const StyledSlider = styled(Slider)`
  .ant-slider-handle {
    border: 2px solid ${({ theme }) => theme["@primary"]};
  }
`;

const CandidateForm = ({
  t,
  i18n,
  form,
  loading,
  onSubmit,
  isDisabled,
  fetchSkills,
  newOpportunity,
  opportunity,
  universities = [],
  professionList = [],
  skillList = [],
  companyCountry,
  goToPreviousStep,
  loadingSkills,
  fieldsOfStudyList,
  section,
  setForm
}) => {
  const locale = ACCEPT_LANGUAGE[i18n.language] || ACCEPT_LANGUAGE.DEFAULT;
  moment.locale(locale);

  const countryOptions = R.pipe(
    getCountries,
    R.toPairs,
    R.map(([value, label]) => ({ value, label })),
    sortOptions
  )();

  const languageOptions = R.pipe(
    getLanguages,
    R.toPairs,
    R.map(([value, label]) => ({ value, label })),
    sortOptions
  )();

  const fieldsOfStudyOptions = useMemo(
    () =>
      R.pipe(
        R.map(item => ({
          label: item[locale] || item.field,
          value: item.field
        })),
        sortOptions
      )(fieldsOfStudyList),
    [fieldsOfStudyList, locale]
  );

  const skillOptions = useMemo(
    () =>
      R.pipe(
        R.map(item => ({
          label: item[locale] || item.skill,
          value: item.skill
        })),
        sortOptions
      )(skillList),
    [skillList, locale]
  );

  const professionOptions = useMemo(
    () =>
      R.pipe(
        R.map(item => ({
          label: item[locale] || item.label,
          value: item.id
        })),
        sortOptions
      )(professionList),
    [professionList, locale]
  );

  const sectionName = t("opportunity:candidates.sectionName");
  const ref = useRef(null);
  useEffect(() => {
    if (section && section === sectionName && ref.current) {
      ref.current.scrollIntoView();
    }
  }, [section, sectionName]);
  const [universitySearch, setUniversitySearch] = useState({
    searchString: "",
    results: []
  });

  useEffect(() => {
    setForm && !newOpportunity && setForm(form.getFieldsValue());
  }, [form, newOpportunity, setForm]);

  const onSearchUniversity = s => {
    if (s === "") {
      return setUniversitySearch({
        searchString: s,
        results: []
      });
    }

    const results = filter(R.uniq(universities), s, { maxResults: 20 });
    setUniversitySearch({
      searchString: s,
      results: results
    });
  };

  return (
    <CandidateFormWrapper ref={ref}>
      <Content>
        <h2>{t("opportunity:candidates.title")}</h2>
        <p>
          {t("opportunity:candidates.subtitle1")}
          <br />
          {t("opportunity:candidates.subtitle2")}
        </p>
        <Form layout="vertical" onSubmit={onSubmit} hideRequiredMark={true}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <FormItem
                form={form}
                name="professions"
                label={t("opportunity:candidates.profession")}
                render={
                  <Select
                    filterOption
                    disabled={isDisabled}
                    mode="multiple"
                    optionFilterProp="children"
                    onChange={professions =>
                      fetchSkills({ country: companyCountry, professions })
                    }
                  >
                    {professionOptions.map(({ value, label }) => (
                      <Select.Option key={value} value={value}>
                        {label}
                      </Select.Option>
                    ))}
                  </Select>
                }
              />

              <FormItem
                label={t("global:nationalities")}
                name="nationalities"
                form={form}
                render={
                  <Select
                    data-test="nationalities-input"
                    disabled={isDisabled}
                    mode="multiple"
                    optionFilterProp="children"
                    filterOption={(inputValue, option) =>
                      normalizeText(option.props.children).includes(
                        normalizeText(inputValue)
                      )
                    }
                  >
                    {countryOptions.map(({ value, label }) => (
                      <Select.Option key={value} value={value}>
                        {label}
                      </Select.Option>
                    ))}
                  </Select>
                }
              />
              <FormItem
                label={t("opportunity:details.location")}
                name="geolocations"
                form={form}
                render={<MultipleAreaLocation />}
              />

              <Toggle
                disabled={isDisabled}
                defaultToggled={
                  !R.either(R.isNil, R.isEmpty)(opportunity.ageRange)
                }
                onChange={checked =>
                  form.setFieldsValue({ ageRange: checked ? [16, 50] : [] })
                }
              >
                {({ toggle, toggled }) => (
                  <FormItem
                    label={
                      <div style={{ display: "flex" }}>
                        <div className="slider-option" style={{ width: 170 }}>
                          {t("opportunity:candidates.age")} {toggle}
                        </div>
                        <ExcludingFilterInfo />
                      </div>
                    }
                    name="ageRange"
                    form={form}
                    render={
                      toggled ? (
                        <StyledSlider
                          range
                          min={16}
                          max={90}
                          step={1}
                          disabled={isDisabled || !toggled}
                          tipFormatter={value =>
                            `${value} ${t("opportunity:candidates.years")}`
                          }
                        />
                      ) : (
                        <div />
                      )
                    }
                  />
                )}
              </Toggle>

              <Toggle
                disabled={isDisabled}
                defaultToggled={
                  !R.either(R.isNil, R.isEmpty)(opportunity.experienceRange)
                }
                onChange={checked =>
                  form.setFieldsValue({
                    experienceRange: checked ? [0, 10] : []
                  })
                }
              >
                {({ toggle, toggled }) => (
                  <FormItem
                    label={
                      <div className="slider-option">
                        {t("opportunity:candidates.experience")} {toggle}
                      </div>
                    }
                    name="experienceRange"
                    form={form}
                    render={
                      toggled ? (
                        <Range
                          disabled={isDisabled}
                          options={experiences.map(value => ({
                            label: t(
                              `catalogs:experience.${value
                                .toString()
                                .replace(".", ",")}`
                            ),
                            value
                          }))}
                        />
                      ) : (
                        <div />
                      )
                    }
                  />
                )}
              </Toggle>
            </Col>
            <Col xs={24} md={12}>
              <FormItem
                label={
                  <div style={{ display: "flex" }}>
                    {t("opportunity:candidates.educationLevelsAdvanced")}
                    <ExcludingFilterInfo />
                  </div>
                }
                name="educationLevelsAdvanced"
                form={form}
                rules={[
                  {
                    validator: (_, value, callback) => {
                      if (typeof value === "undefined") {
                        callback();
                        return;
                      }
                      if (Array.isArray(value)) {
                        value.forEach(({ from, to, progress, level }) => {
                          if (!level) {
                            callback(t("opportunity:details.noLevelSelected"));
                            return;
                          }
                          if (level.match("avanzado")) {
                            if (
                              Array.isArray(progress) &&
                              progress.length !== 2
                            ) {
                              callback(t("opportunity:details.invalidRange"));
                              return;
                            }
                          }
                          if (level.match("completo")) {
                            if (to && !from) {
                              callback(
                                t("opportunity:details.missingStartDate")
                              );
                              return;
                            }
                            if (from && !to) {
                              callback(t("opportunity:details.missingEndDate"));
                              return;
                            }
                          }
                        });
                        callback();
                      }
                    }
                  }
                ]}
                decoratorProps={{
                  validateTrigger: false
                }}
                render={
                  <Multi
                    disabled={isDisabled}
                    deleteBtnStyle={{
                      marginTop: 31,
                      marginBottom: "auto",
                      marginRight: 0,
                      marginLeft: 8
                    }}
                    lineSeparator
                    keyExtractor={({ level }, index) =>
                      `${index}-${level || "not-defined"}`
                    }
                  >
                    <Formation
                      options={R.difference(
                        EDUCATION_LEVELS,
                        R.pipe(
                          R.defaultTo([]),
                          R.map(R.propOr("", "level"))
                        )(form.getFieldValue("educationLevelsAdvanced"))
                      )}
                      t={t}
                    />
                  </Multi>
                }
              />

              <FormItem
                label={
                  <div style={{ display: "flex" }}>
                    {t("global:studies")}
                    <ExcludingFilterInfo />
                  </div>
                }
                name="fieldsOfStudy"
                form={form}
                render={
                  <Select
                    data-test="studies-input"
                    disabled={isDisabled}
                    mode="multiple"
                    optionFilterProp="children"
                    filterOption={(inputValue, option) =>
                      normalizeText(option.props.children).includes(
                        normalizeText(inputValue)
                      )
                    }
                  >
                    {fieldsOfStudyOptions.map(({ label, value }) => (
                      <Select.Option key={value} value={value}>
                        {label}
                      </Select.Option>
                    ))}
                  </Select>
                }
              />

              <FormItem
                label={t("global:universities")}
                name="universities"
                form={form}
                help={
                  form.getFieldValue("country") === "ALL" &&
                  t("opportunity:details.selectCountry")
                }
                render={
                  <Select
                    disabled={
                      isDisabled || form.getFieldValue("country") === "ALL"
                    }
                    mode="multiple"
                    optionFilterProp="children"
                    onSearch={onSearchUniversity}
                    notFoundContent={
                      <EmptyUniversities
                        description={
                          universitySearch.searchString === ""
                            ? t("opportunity:details.searchUniversity")
                            : t("opportunity:details.universityNotFound")
                        }
                      />
                    }
                  >
                    {universitySearch.results.map(university => (
                      <Select.Option
                        key={`univ_${university}`}
                        value={university}
                      >
                        {university}
                      </Select.Option>
                    ))}
                  </Select>
                }
              />

              <FormItem
                label={t("opportunity:candidates.skills")}
                name="skillsReq"
                form={form}
                render={
                  <Select
                    filterOption
                    disabled={isDisabled}
                    mode="multiple"
                    optionFilterProp="children"
                    loading={loadingSkills}
                  >
                    {skillOptions.map(({ value, label }) => (
                      <Select.Option key={value} value={value}>
                        {label}
                      </Select.Option>
                    ))}
                  </Select>
                }
              />

              <FormItem
                label={
                  <div style={{ display: "flex" }}>
                    {t("opportunity:candidates.languages")}
                    <ExcludingFilterInfo />
                  </div>
                }
                name="languages"
                form={form}
                rules={[
                  {
                    validator: (_, value, callback) => {
                      if (R.any(lang => !lang.name || !lang.level)(value)) {
                        return callback(
                          t("opportunity:candidates.languagesValidation")
                        );
                      }
                      return callback();
                    }
                  }
                ]}
                render={
                  <Multi disabled={isDisabled}>
                    <StatusSelect
                      disabled={isDisabled}
                      valueProp="name"
                      statusProp="level"
                      options={{
                        values: languageOptions,
                        statuses: [
                          { value: 1, label: t("catalogs:languages.levels.1") },
                          { value: 2, label: t("catalogs:languages.levels.2") },
                          { value: 3, label: t("catalogs:languages.levels.3") },
                          { value: 4, label: t("catalogs:languages.levels.4") },
                          { value: 5, label: t("catalogs:languages.levels.5") }
                        ]
                      }}
                    />
                  </Multi>
                }
              />
            </Col>
          </Row>

          {!isDisabled && newOpportunity && (
            <Row type="flex" justify={newOpportunity ? "space-between" : "end"}>
              {newOpportunity && (
                <Button
                  size="lg"
                  type="quaternary"
                  disabled={loading}
                  onClick={goToPreviousStep}
                >
                  {t("global:back")}
                </Button>
              )}
              <Button
                size="lg"
                loading={loading}
                type="primary"
                htmlType="submit"
              >
                {newOpportunity ? t("global:next") : t("global:save")}
              </Button>
            </Row>
          )}
        </Form>
      </Content>
    </CandidateFormWrapper>
  );
};

const enhancer = compose(
  withTranslation(["opportunity", "global"]),
  connect(
    state => ({
      skillList: FETCH_SKILLS.getResponse(state) || [],
      professionList: FETCH_PROFESSIONS.getResponse(state) || [],
      fieldsOfStudyList: FETCH_STUDIES.getResponse(state) || [],
      universities: R.map(
        R.prop("name"),
        FETCH_UNIVERSITIES.getResponse(state) || []
      ),
      newOpportunity: state.opportunity.newOpportunity,
      companyCountry: state.company.country
    }),
    {
      saveOpportunityCandidate,
      goToPreviousStep: GO_TO_PREVIOUS_WIZARD_STEP.run,
      fetchSkills
    }
  ),
  dispatchOnMount([
    ({ companyCountry, opportunity: { professions = [] } }) =>
      fetchSkills({ country: companyCountry, professions })
  ]),
  withForm({
    selector: (state, { opportunity }) =>
      R.pipe(
        R.pick(FORMS_KEYS.candidate),
        R.when(
          R.propSatisfies(R.isNil, "country"),
          R.assoc("country", state.company.country || "ALL")
        ),
        R.when(R.propEq("country", ""), R.assoc("country", "ALL"))
      )(opportunity),
    onSubmit: (formData, { saveOpportunityCandidate, opportunity, form }) => {
      deleteOpportunityStoredFilters(opportunity);
      form.validateFields(err => {
        if (!err) {
          gtag.event({
            category: "VacancyDetail",
            action: "VacancyDetail.Candidates-Save",
            label: opportunity.id
          });
          saveOpportunityCandidate({
            id: opportunity.id,
            formData: {
              ...formData,
              country: formData.country === "ALL" ? "" : formData.country
            }
          });
        }
      });
    }
  }),
  isLoading(FETCH_SKILLS, { prop: "loadingSkills" }),
  isLoading(SAVE_OPPORTUNITY_CANDIDATE)
);

export default enhancer(CandidateForm);
